import { useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Typography, Card, CardContent, Container, Button, Chip, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import LockIcon from "@mui/icons-material/Lock";
import LinkIcon from "@mui/icons-material/Link";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import BoltIcon from "@mui/icons-material/Bolt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import HistoryIcon from "@mui/icons-material/History";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, loginWithGoogle } = useAuth();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    {
      icon: <LockIcon sx={{ color: "primary.main" }} />,
      title: t("home.feature1Title"),
      desc: t("home.feature1Desc"),
    },
    {
      icon: <LinkIcon sx={{ color: "secondary.main" }} />,
      title: t("home.feature2Title"),
      desc: t("home.feature2Desc"),
    },
    {
      icon: <AutoDeleteIcon sx={{ color: "warning.main" }} />,
      title: t("home.feature3Title"),
      desc: t("home.feature3Desc"),
    },
    {
      icon: <BoltIcon sx={{ color: "success.main" }} />,
      title: t("home.feature4Title"),
      desc: t("home.feature4Desc"),
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 3, md: 6 }}
          sx={{
            alignItems: "center",
          }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Chip label={t("app.subtitle")} color="secondary" sx={{ mb: 2 }} />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", md: "3rem" },
                lineHeight: 1.15,
              }}
            >
              {t("home.hero")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mb: 3,
                lineHeight: 1.75,
              }}
            >
              {t("home.heroSubtitle")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button variant="contained" color="secondary" size="large" onClick={loginWithGoogle}>
                {t("home.loginWithGoogle")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/preview"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  textAlign: "center",
                  justifyContent: "center",
                }}
              >
                {t("preview.cta")}
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ maxWidth: 520, mx: "auto" }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  {t("home.howItWorksTitle")}
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <FileUploadIcon color="secondary" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{t("home.step1")}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <LinkIcon color="secondary" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{t("home.step2")}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <HistoryIcon color="secondary" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{t("home.step3")}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 5, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              mb: 1.5,
            }}
          >
            {t("home.featuresTitle")}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mb: 4,
            }}
          >
            {t("home.featuresSubtitle")}
          </Typography>
          <Grid container spacing={2.5}>
            {features.map((feature) => (
              <Grid size={{ xs: 12, sm: 6 }} key={feature.title}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
